// ============================================
// MENTOR PORTAL MOCK DATA
// ============================================

export type DropRiskLevel = 'low' | 'medium' | 'high'
export type ApprovalStatus = 'pending' | 'approved' | 'rejected'
export type TaskType = 'reading' | 'mcq' | 'test' | 'revision'
export type TaskStatus = 'assigned' | 'in-progress' | 'completed' | 'pending-review'
export type PerformanceTrend = 'strongly-improving' | 'improving' | 'stable' | 'declining' | 'strongly-declining'

// Subject Types with Biology Subdivisions
export type BiologySubject = 'Botany' | 'Zoology'
export type MainSubject = 'Physics' | 'Chemistry' | 'Biology'
export type Subject = MainSubject | BiologySubject

// Test Management Types
export type TestCategory = 'chapter' | 'daily' | 'weekly' | 'monthly' | 'full'
export type TestManagementStatus = 'draft' | 'published' | 'upcoming' | 'active' | 'completed'
export type QuestionDifficulty = 'easy' | 'medium' | 'hard'
export type SectionStatus = 'open' | 'locked' | 'submitted' | 'approved'

// ============================================
// INTERFACES
// ============================================

// Mentor Profile with Subject Assignments
export interface MentorProfile {
    id: string
    name: string
    email: string
    assignedSubjects: Subject[] // e.g., ['Botany'] or ['Physics', 'Chemistry']
    role: 'mentor' | 'admin'
}

export interface MentorStudent {
    id: string
    name: string
    targetYear: number
    batch: string
    courseId?: string
    courseName?: string
    profileImage?: string
    lastActivity: string // e.g., "2h ago"
    lastActivityTimestamp: Date
    todayHoursSubmitted: boolean
    todayHoursReported?: number
    todayHoursApproved?: number
    weeklyAvgHours: number
    disciplineScore: number // 1-10
    currentStreak: number
    weakSubjects: string[]
    dropRisk: DropRiskLevel
    performanceTrend: PerformanceTrend
    trendValue: number // e.g., +20, -15
    avgTestScore: number // out of 720
    contactNumber?: string
    email?: string
}

export interface AssignedTask {
    id: string
    studentId: string
    studentName?: string
    date: string
    subject: 'Physics' | 'Chemistry' | 'Biology'
    chapterTopic: string
    taskType: TaskType
    expectedDuration: number // in hours
    priority: 'low' | 'medium' | 'high'
    status: TaskStatus
    isPublished: boolean
    completedAt?: string
    proofNote?: string
}

export interface CompletedTask {
    id: string
    title: string
    subject: string
    duration: number
    proofNote?: string
}

export interface StudentDailyReport {
    id: string
    studentId: string
    studentName: string
    date: string
    reportedHours: number
    approvedHours?: number
    subjectBreakdown: {
        physics: number
        chemistry: number
        biology: number
    }
    tasksCompleted: CompletedTask[]
    workDescription: string
    challenges: string
    mentorFeedback?: string
    approvalStatus: ApprovalStatus
    submittedAt: string
    reviewedAt?: string
}

export interface StudentTestResult {
    testId: string
    studentId: string
    studentName: string
    testName: string
    date: string
    score: number
    totalMarks: number
    rank?: number
    subjectWise: {
        physics: number
        chemistry: number
        biology: number
    }
}

export interface ChatMessage {
    id: string
    senderId: string
    senderName: string
    senderRole: 'mentor' | 'student'
    recipientId: string
    message: string
    timestamp: Date
    read: boolean
}

export interface Announcement {
    id: string
    title: string
    content: string
    createdAt: Date
    targetBatch?: string
    targetStudents?: string[]
    readBy: string[]
}

// Test Management Interfaces
export interface TestSection {
    id: string
    subject: 'Physics' | 'Chemistry' | 'Biology' | 'Botany' | 'Zoology'
    assignedMentor?: string
    assignedMentorName?: string
    status: SectionStatus
    questionCount: number
}

export interface Test {
    id: string
    title: string
    category: TestCategory
    subjects: string[]
    chapters?: string[]
    duration: number // in minutes
    totalMarks: number
    negativeMarking: { correct: number; incorrect: number }
    schedule: {
        startTime: string
        endTime: string
        lateEntryAllowed: boolean
        lateEntryWindow?: number // in minutes
    }
    attemptsAllowed: number
    visibility: {
        type: 'batch' | 'course' | 'all'
        targets?: string[]
    }
    status: TestManagementStatus
    sections?: TestSection[]
    questionCount: number
    createdBy: string
    createdAt: string
}

export interface QuestionBankItem {
    id: string
    subject: 'Physics' | 'Chemistry' | 'Biology' | 'Botany' | 'Zoology'
    chapter: string
    difficulty: QuestionDifficulty
    text: string
    options: string[]
    correctOption: number
    explanation?: string
    timeSuggestion?: number // in seconds
    tags?: string[]
    createdBy: string
    createdAt: string
}

export interface QuestionBank {
    id: string
    title: string
    subject: 'Physics' | 'Chemistry' | 'Biology' | 'Botany' | 'Zoology'
    chapter: string
    questionCount: number
    difficulty: QuestionDifficulty
    status: 'draft' | 'published'
    createdAt: string
    publishedTo: string[]
}

// ============================================
// CHAPTER MAPPINGS
// ============================================

export const chaptersBySubject: Record<string, string[]> = {
    Physics: ['Mechanics', 'Thermodynamics', 'Optics', 'Electrostatics', 'Magnetism', 'Modern Physics'],
    Chemistry: ['Organic Chemistry', 'Inorganic Chemistry', 'Physical Chemistry', 'Electrochemistry'],
    Botany: ['Plant Physiology', 'Plant Taxonomy', 'Morphology of Flowering Plants', 'Anatomy of Flowering Plants', 'Plant Kingdom', 'Cell Biology'],
    Zoology: ['Human Physiology', 'Animal Diversity', 'Structural Organization in Animals', 'Ecology', 'Evolution', 'Genetics']
}

// ============================================
// MENTOR PROFILES
// ============================================

export const mockMentorProfiles: MentorProfile[] = [
    {
        id: 'mentor-001',
        name: 'Dr. Sarah Johnson',
        email: 'sarah.johnson@neet.edu',
        assignedSubjects: ['Physics'],
        role: 'mentor'
    },
    {
        id: 'mentor-002',
        name: 'Prof. Rajesh Kumar',
        email: 'rajesh.kumar@neet.edu',
        assignedSubjects: ['Chemistry'],
        role: 'mentor'
    },
    {
        id: 'mentor-003',
        name: 'Dr. Meera Patel',
        email: 'meera.patel@neet.edu',
        assignedSubjects: ['Botany'],
        role: 'mentor'
    },
    {
        id: 'mentor-004',
        name: 'Dr. Anil Sharma',
        email: 'anil.sharma@neet.edu',
        assignedSubjects: ['Zoology'],
        role: 'mentor'
    },
    {
        id: 'mentor-admin',
        name: 'Admin User',
        email: 'admin@neet.edu',
        assignedSubjects: ['Physics', 'Chemistry', 'Botany', 'Zoology'],
        role: 'admin'
    }
]

// Current logged-in mentor (can be changed for testing)
export const currentMentor: MentorProfile = mockMentorProfiles[2] // Dr. Meera (Botany mentor)

// ============================================
// MOCK STUDENTS DATA
// ============================================

export const mockMentorStudents: MentorStudent[] = [
    {
        id: 'st-001',
        name: 'Arun Kumar',
        targetYear: 2025,
        batch: 'NEET 2025 Batch A',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '2h ago',
        lastActivityTimestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 6.5,
        weeklyAvgHours: 6.2,
        disciplineScore: 8,
        currentStreak: 5,
        weakSubjects: ['Chemistry'],
        dropRisk: 'low',
        performanceTrend: 'improving',
        trendValue: 20,
        avgTestScore: 580,
        email: 'arun@example.com',
    },
    {
        id: 'st-002',
        name: 'Priya Sharma',
        targetYear: 2025,
        batch: 'NEET 2025 Batch A',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '5h ago',
        lastActivityTimestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 7.0,
        weeklyAvgHours: 7.5,
        disciplineScore: 9,
        currentStreak: 12,
        weakSubjects: ['Physics'],
        dropRisk: 'low',
        performanceTrend: 'strongly-improving',
        trendValue: 35,
        avgTestScore: 650,
        email: 'priya@example.com',
    },
    {
        id: 'st-003',
        name: 'Rahul Verma',
        targetYear: 2025,
        batch: 'NEET 2025 Batch A',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '2d ago',
        lastActivityTimestamp: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
        todayHoursSubmitted: false,
        weeklyAvgHours: 4.2,
        disciplineScore: 5,
        currentStreak: 0,
        weakSubjects: ['Biology', 'Chemistry'],
        dropRisk: 'high',
        performanceTrend: 'declining',
        trendValue: -25,
        avgTestScore: 480,
        email: 'rahul@example.com',
    },
    {
        id: 'st-004',
        name: 'Ananya Patel',
        targetYear: 2025,
        batch: 'NEET 2025 Batch B',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '1h ago',
        lastActivityTimestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 5.5,
        weeklyAvgHours: 5.8,
        disciplineScore: 7,
        currentStreak: 3,
        weakSubjects: [],
        dropRisk: 'low',
        performanceTrend: 'stable',
        trendValue: 5,
        avgTestScore: 590,
        email: 'ananya@example.com',
    },
    {
        id: 'st-005',
        name: 'Rohan Singh',
        targetYear: 2025,
        batch: 'NEET 2025 Batch A',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '3h ago',
        lastActivityTimestamp: new Date(Date.now() - 3 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 6.0,
        weeklyAvgHours: 6.5,
        disciplineScore: 8,
        currentStreak: 7,
        weakSubjects: ['Chemistry'],
        dropRisk: 'low',
        performanceTrend: 'improving',
        trendValue: 15,
        avgTestScore: 610,
        email: 'rohan@example.com',
    },
    {
        id: 'st-006',
        name: 'Sneha Gupta',
        targetYear: 2025,
        batch: 'NEET 2025 Batch B',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '12h ago',
        lastActivityTimestamp: new Date(Date.now() - 12 * 60 * 60 * 1000),
        todayHoursSubmitted: false,
        weeklyAvgHours: 5.0,
        disciplineScore: 6,
        currentStreak: 2,
        weakSubjects: ['Physics'],
        dropRisk: 'medium',
        performanceTrend: 'stable',
        trendValue: -5,
        avgTestScore: 550,
        email: 'sneha@example.com',
    },
    {
        id: 'st-007',
        name: 'Vikas Reddy',
        targetYear: 2025,
        batch: 'NEET 2025 Batch A',
        courseId: 'c1',
        courseName: 'Complete NEET 2025 Preparation',
        lastActivity: '1h ago',
        lastActivityTimestamp: new Date(Date.now() - 1 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 7.5,
        weeklyAvgHours: 7.2,
        disciplineScore: 9,
        currentStreak: 10,
        weakSubjects: [],
        dropRisk: 'low',
        performanceTrend: 'strongly-improving',
        trendValue: 40,
        avgTestScore: 680,
        email: 'vikas@example.com',
    },
    {
        id: 'st-008',
        name: 'Kavya Joshi',
        targetYear: 2025,
        batch: 'NEET 2025 Batch B',
        courseId: 'c2',
        courseName: 'Physics Masterclass',
        lastActivity: '4h ago',
        lastActivityTimestamp: new Date(Date.now() - 4 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 6.5,
        weeklyAvgHours: 6.0,
        disciplineScore: 7,
        currentStreak: 4,
        weakSubjects: ['Biology'],
        dropRisk: 'low',
        performanceTrend: 'stable',
        trendValue: 2,
        avgTestScore: 595,
        email: 'kavya@example.com',
    },
    {
        id: 'st-009',
        name: 'Arjun Mehta',
        targetYear: 2025,
        batch: 'NEET 2025 Batch A',
        courseId: 'c2',
        courseName: 'Physics Masterclass',
        lastActivity: '1d ago',
        lastActivityTimestamp: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
        todayHoursSubmitted: false,
        weeklyAvgHours: 5.5,
        disciplineScore: 6,
        currentStreak: 0,
        weakSubjects: ['Physics', 'Biology'],
        dropRisk: 'medium',
        performanceTrend: 'declining',
        trendValue: -12,
        avgTestScore: 520,
        email: 'arjun@example.com',
    },
    {
        id: 'st-010',
        name: 'Divya Nair',
        targetYear: 2025,
        batch: 'NEET 2025 Batch B',
        courseId: 'c2',
        courseName: 'Physics Masterclass',
        lastActivity: '2h ago',
        lastActivityTimestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        todayHoursSubmitted: true,
        todayHoursReported: 8.0,
        weeklyAvgHours: 7.8,
        disciplineScore: 10,
        currentStreak: 15,
        weakSubjects: [],
        dropRisk: 'low',
        performanceTrend: 'strongly-improving',
        trendValue: 45,
        avgTestScore: 700,
        email: 'divya@example.com',
    },
]

// ============================================
// MOCK DAILY REPORTS (Pending Approvals)
// ============================================

export const mockPendingReports: StudentDailyReport[] = [
    {
        id: 'rpt-001',
        studentId: 'st-001',
        studentName: 'Arun Kumar',
        date: 'Oct 26, 2024',
        reportedHours: 6.5,
        subjectBreakdown: {
            physics: 2.5,
            chemistry: 2.0,
            biology: 2.0,
        },
        tasksCompleted: [
            { id: 't1', title: 'Thermodynamics Chapter 4 Reading', subject: 'Physics', duration: 1.5 },
            { id: 't2', title: '50 MCQs from Organic Chemistry', subject: 'Chemistry', duration: 1.0, proofNote: 'Completed all with 80% accuracy' },
            { id: 't3', title: 'Human Physiology Revision', subject: 'Biology', duration: 2.0 },
        ],
        workDescription: 'Focused well on Physics today. Chemistry MCQs were challenging but completed all. Biology revision took longer than expected.',
        challenges: 'Struggled with some organic chemistry mechanisms.',
        approvalStatus: 'pending',
        submittedAt: '2024-10-26T20:30:00',
    },
    {
        id: 'rpt-002',
        studentId: 'st-002',
        studentName: 'Priya Sharma',
        date: 'Oct 26, 2024',
        reportedHours: 7.0,
        subjectBreakdown: {
            physics: 3.0,
            chemistry: 2.0,
            biology: 2.0,
        },
        tasksCompleted: [
            { id: 't4', title: 'Electrostatics Chapter 5', subject: 'Physics', duration: 2.0 },
            { id: 't5', title: 'Chemical Bonding MCQs', subject: 'Chemistry', duration: 1.5 },
            { id: 't6', title: 'Cell Biology Revision', subject: 'Biology', duration: 2.0 },
            { id: 't7', title: 'Previous Year Questions', subject: 'Physics', duration: 1.0 },
        ],
        workDescription: 'Excellent progress today. Completed all assigned tasks plus additional PYQs.',
        challenges: 'None',
        approvalStatus: 'pending',
        submittedAt: '2024-10-26T21:00:00',
    },
    {
        id: 'rpt-003',
        studentId: 'st-005',
        studentName: 'Rohan Singh',
        date: 'Oct 26, 2024',
        reportedHours: 6.0,
        subjectBreakdown: {
            physics: 2.0,
            chemistry: 2.0,
            biology: 2.0,
        },
        tasksCompleted: [
            { id: 't8', title: 'Mechanics Revision', subject: 'Physics', duration: 2.0 },
            { id: 't9', title: 'Organic Chemistry Named Reactions', subject: 'Chemistry', duration: 2.0 },
            { id: 't10', title: 'Genetics MCQs', subject: 'Biology', duration: 2.0 },
        ],
        workDescription: 'Steady progress. All tasks completed on time.',
        challenges: 'Need more practice in organic mechanisms.',
        approvalStatus: 'pending',
        submittedAt: '2024-10-26T20:00:00',
    },
]

// ============================================
// MOCK ASSIGNED TASKS
// ============================================

export const mockAssignedTasks: AssignedTask[] = [
    {
        id: 'task-001',
        studentId: 'st-001',
        studentName: 'Arun Kumar',
        date: 'Oct 27, 2024',
        subject: 'Physics',
        chapterTopic: 'Thermodynamics Chapter 5',
        taskType: 'reading',
        expectedDuration: 1.5,
        priority: 'high',
        status: 'assigned',
        isPublished: true,
    },
    {
        id: 'task-002',
        studentId: 'st-001',
        studentName: 'Arun Kumar',
        date: 'Oct 27, 2024',
        subject: 'Chemistry',
        chapterTopic: '100 MCQs - Inorganic Chemistry',
        taskType: 'mcq',
        expectedDuration: 2.0,
        priority: 'high',
        status: 'assigned',
        isPublished: true,
    },
]

// ============================================
// MOCK TEST RESULTS
// ============================================

export const mockTestResults: StudentTestResult[] = [
    {
        testId: 'test-001',
        studentId: 'st-001',
        studentName: 'Arun Kumar',
        testName: 'Weekly Mock Test 4',
        date: 'Oct 20, 2024',
        score: 580,
        totalMarks: 720,
        rank: 45,
        subjectWise: { physics: 180, chemistry: 190, biology: 210 },
    },
    {
        testId: 'test-001',
        studentId: 'st-002',
        studentName: 'Priya Sharma',
        testName: 'Weekly Mock Test 4',
        date: 'Oct 20, 2024',
        score: 650,
        totalMarks: 720,
        rank: 12,
        subjectWise: { physics: 210, chemistry: 220, biology: 220 },
    },
    {
        testId: 'test-001',
        studentId: 'st-003',
        studentName: 'Rahul Verma',
        testName: 'Weekly Mock Test 4',
        date: 'Oct 20, 2024',
        score: 480,
        totalMarks: 720,
        rank: 89,
        subjectWise: { physics: 150, chemistry: 160, biology: 170 },
    },
]

// ============================================
// MOCK CHAT MESSAGES
// ============================================

export const mockChatMessages: ChatMessage[] = [
    {
        id: 'msg-001',
        senderId: 'st-001',
        senderName: 'Arun Kumar',
        senderRole: 'student',
        recipientId: 'mentor-001',
        message: 'Hi Dr. Sarah, I need help with Thermodynamics chapter 4. The concept of entropy is confusing.',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
        read: false,
    },
    {
        id: 'msg-002',
        senderId: 'st-002',
        senderName: 'Priya Sharma',
        senderRole: 'student',
        recipientId: 'mentor-001',
        message: 'Thank you for the feedback! I\'ll focus more on Physics this week.',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000),
        read: true,
    },
]

// ============================================
// MOCK TESTS DATA
// ============================================

export const mockTests: Test[] = [
    {
        id: 'test-ch-001',
        title: 'Thermodynamics Chapter Test',
        category: 'chapter',
        subjects: ['Physics'],
        chapters: ['Thermodynamics'],
        duration: 60,
        totalMarks: 120,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-10T10:00:00',
            endTime: '2026-02-10T11:00:00',
            lateEntryAllowed: true,
            lateEntryWindow: 10
        },
        attemptsAllowed: 1,
        visibility: { type: 'batch', targets: ['NEET 2025 Batch A'] },
        status: 'upcoming',
        questionCount: 30,
        createdBy: 'Dr. Sarah',
        createdAt: '2026-02-05'
    },
    {
        id: 'test-ch-002',
        title: 'Organic Chemistry - Named Reactions',
        category: 'chapter',
        subjects: ['Chemistry'],
        chapters: ['Organic Chemistry'],
        duration: 45,
        totalMarks: 80,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-12T14:00:00',
            endTime: '2026-02-12T14:45:00',
            lateEntryAllowed: false
        },
        attemptsAllowed: 1,
        visibility: { type: 'batch', targets: ['NEET 2025 Batch A', 'NEET 2025 Batch B'] },
        status: 'draft',
        questionCount: 20,
        createdBy: 'Prof. Kumar',
        createdAt: '2026-02-06'
    },
    {
        id: 'test-daily-001',
        title: 'Daily Practice - Physics',
        category: 'daily',
        subjects: ['Physics'],
        duration: 30,
        totalMarks: 40,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-08T08:00:00',
            endTime: '2026-02-08T23:59:00',
            lateEntryAllowed: true
        },
        attemptsAllowed: 3,
        visibility: { type: 'all' },
        status: 'active',
        questionCount: 10,
        createdBy: 'Dr. Sarah',
        createdAt: '2026-02-08'
    },
    {
        id: 'test-weekly-001',
        title: 'Weekly Mock Test 5',
        category: 'weekly',
        subjects: ['Physics', 'Chemistry', 'Biology'],
        duration: 180,
        totalMarks: 720,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-09T10:00:00',
            endTime: '2026-02-09T13:00:00',
            lateEntryAllowed: true,
            lateEntryWindow: 15
        },
        attemptsAllowed: 1,
        visibility: { type: 'batch', targets: ['NEET 2025 Batch A'] },
        status: 'upcoming',
        sections: [
            { id: 'sec-p', subject: 'Physics', assignedMentor: 'mentor-001', assignedMentorName: 'Dr. Sarah Johnson', status: 'approved', questionCount: 45 },
            { id: 'sec-c', subject: 'Chemistry', assignedMentor: 'mentor-002', assignedMentorName: 'Prof. Rajesh Kumar', status: 'locked', questionCount: 45 },
            { id: 'sec-bot', subject: 'Botany', assignedMentor: 'mentor-003', assignedMentorName: 'Dr. Meera Patel', status: 'submitted', questionCount: 45 },
            { id: 'sec-zoo', subject: 'Zoology', assignedMentor: 'mentor-004', assignedMentorName: 'Dr. Anil Sharma', status: 'approved', questionCount: 45 }
        ],
        questionCount: 180,
        createdBy: 'Admin',
        createdAt: '2026-02-01'
    },
    {
        id: 'test-monthly-001',
        title: 'February Grand Test',
        category: 'monthly',
        subjects: ['Physics', 'Chemistry', 'Biology'],
        duration: 200,
        totalMarks: 720,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-28T09:00:00',
            endTime: '2026-02-28T12:20:00',
            lateEntryAllowed: false
        },
        attemptsAllowed: 1,
        visibility: { type: 'all' },
        status: 'draft',
        sections: [
            { id: 'sec-p2', subject: 'Physics', status: 'open', questionCount: 0 },
            { id: 'sec-c2', subject: 'Chemistry', status: 'open', questionCount: 0 },
            { id: 'sec-b2', subject: 'Biology', status: 'open', questionCount: 0 }
        ],
        questionCount: 0,
        createdBy: 'Admin',
        createdAt: '2026-02-01'
    },
    {
        id: 'test-full-001',
        title: 'NEET Full Mock - Series 1',
        category: 'full',
        subjects: ['Physics', 'Chemistry', 'Biology'],
        duration: 200,
        totalMarks: 720,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-15T09:00:00',
            endTime: '2026-02-15T12:20:00',
            lateEntryAllowed: false
        },
        attemptsAllowed: 1,
        visibility: { type: 'course', targets: ['NEET 2025 Premium'] },
        status: 'published',
        questionCount: 180,
        createdBy: 'Admin',
        createdAt: '2026-01-20'
    },
    {
        id: 'test-weekly-completed',
        title: 'Weekly Mock Test 4',
        category: 'weekly',
        subjects: ['Physics', 'Chemistry', 'Biology'],
        duration: 180,
        totalMarks: 720,
        negativeMarking: { correct: 4, incorrect: 1 },
        schedule: {
            startTime: '2026-02-02T10:00:00',
            endTime: '2026-02-02T13:00:00',
            lateEntryAllowed: true,
            lateEntryWindow: 15
        },
        attemptsAllowed: 1,
        visibility: { type: 'batch', targets: ['NEET 2025 Batch A'] },
        status: 'completed',
        questionCount: 180,
        createdBy: 'Admin',
        createdAt: '2026-01-25'
    }
]

// MOCK QUESTION BANK DATA
// ============================================

export const mockQuestionBanks: QuestionBank[] = [
    {
        id: 'qb-001',
        title: 'Mechanics Fundamentals',
        subject: 'Physics',
        chapter: 'Mechanics',
        questionCount: 50,
        difficulty: 'medium',
        status: 'published',
        createdAt: '2026-01-15',
        publishedTo: ['Long Term', 'Crash Course']
    },
    {
        id: 'qb-002',
        title: 'Organic Chemistry Basics',
        subject: 'Chemistry',
        chapter: 'Organic Chemistry',
        questionCount: 45,
        difficulty: 'hard',
        status: 'published',
        createdAt: '2026-01-20',
        publishedTo: ['All Paid Users'] // "All" mapped to display name
    },
    {
        id: 'qb-003',
        title: 'Cell Biology Practice',
        subject: 'Biology',
        chapter: 'Cell Biology',
        questionCount: 60,
        difficulty: 'easy',
        status: 'draft',
        createdAt: '2026-02-01',
        publishedTo: []
    },
    {
        id: 'qb-004',
        title: 'Thermodynamics Advanced',
        subject: 'Physics',
        chapter: 'Thermodynamics',
        questionCount: 35,
        difficulty: 'hard',
        status: 'published',
        createdAt: '2026-02-05',
        publishedTo: ['Dropper', '2-Year Integrated']
    },
    {
        id: 'qb-005',
        title: 'Plant Physiology Fundamentals',
        subject: 'Botany',
        chapter: 'Plant Physiology',
        questionCount: 45,
        difficulty: 'medium',
        status: 'published',
        createdAt: '2026-02-03',
        publishedTo: ['Long Term', 'Crash Course']
    },
    {
        id: 'qb-006',
        title: 'Human Physiology Practice',
        subject: 'Zoology',
        chapter: 'Human Physiology',
        questionCount: 45,
        difficulty: 'medium',
        status: 'published',
        createdAt: '2026-02-04',
        publishedTo: ['Long Term', 'Crash Course']
    },
    {
        id: 'qb-007',
        title: 'Plant Taxonomy Advanced',
        subject: 'Botany',
        chapter: 'Plant Taxonomy',
        questionCount: 30,
        difficulty: 'hard',
        status: 'draft',
        createdAt: '2026-02-06',
        publishedTo: []
    },
    {
        id: 'qb-008',
        title: 'Animal Diversity',
        subject: 'Zoology',
        chapter: 'Animal Diversity',
        questionCount: 40,
        difficulty: 'easy',
        status: 'published',
        createdAt: '2026-02-02',
        publishedTo: ['All Paid Users']
    }
]

export const mockQuestionBank: QuestionBankItem[] = [
    // Physics Questions
    {
        id: 'q-p-001',
        subject: 'Physics',
        chapter: 'Thermodynamics',
        difficulty: 'medium',
        text: 'The efficiency of a Carnot engine working between 500K and 300K is:',
        options: ['40%', '50%', '60%', '80%'],
        correctOption: 0,
        explanation: 'Efficiency = 1 - T₂/T₁ = 1 - 300/500 = 0.4 = 40%',
        timeSuggestion: 60,
        tags: ['carnot', 'efficiency', 'heat-engine'],
        createdBy: 'Dr. Sarah',
        createdAt: '2026-01-15'
    },
    {
        id: 'q-p-002',
        subject: 'Physics',
        chapter: 'Mechanics',
        difficulty: 'easy',
        text: 'A body of mass 2 kg is moving with a velocity of 5 m/s. Its kinetic energy is:',
        options: ['25 J', '50 J', '10 J', '5 J'],
        correctOption: 0,
        explanation: 'KE = ½mv² = ½ × 2 × 5² = 25 J',
        timeSuggestion: 45,
        tags: ['kinetic-energy', 'mechanics'],
        createdBy: 'Dr. Sarah',
        createdAt: '2026-01-12'
    },
    {
        id: 'q-p-003',
        subject: 'Physics',
        chapter: 'Optics',
        difficulty: 'hard',
        text: 'In Young\'s double slit experiment, if the slit separation is halved and the distance between screen and slits is doubled, the fringe width becomes:',
        options: ['Same', 'Double', 'Four times', 'Half'],
        correctOption: 2,
        explanation: 'Fringe width β = λD/d. If d→d/2 and D→2D, β→4β',
        timeSuggestion: 90,
        tags: ['YDSE', 'interference', 'wave-optics'],
        createdBy: 'Dr. Sarah',
        createdAt: '2026-01-18'
    },
    // Chemistry Questions
    {
        id: 'q-c-001',
        subject: 'Chemistry',
        chapter: 'Organic Chemistry',
        difficulty: 'medium',
        text: 'The IUPAC name of CH₃-CH=CH-CHO is:',
        options: ['But-2-enal', 'But-3-enal', 'Crotonaldehyde', 'Both A and C'],
        correctOption: 0,
        explanation: 'The correct IUPAC name is But-2-enal (crotonaldehyde is the common name)',
        timeSuggestion: 60,
        tags: ['iupac', 'nomenclature', 'aldehydes'],
        createdBy: 'Prof. Kumar',
        createdAt: '2026-01-14'
    },
    {
        id: 'q-c-002',
        subject: 'Chemistry',
        chapter: 'Electrochemistry',
        difficulty: 'easy',
        text: 'The standard electrode potential of Zn²⁺/Zn is:',
        options: ['-0.76V', '+0.76V', '-1.66V', '+1.66V'],
        correctOption: 0,
        explanation: 'Standard reduction potential of Zn²⁺/Zn is -0.76V',
        timeSuggestion: 30,
        tags: ['electrode-potential', 'electrochemistry'],
        createdBy: 'Prof. Kumar',
        createdAt: '2026-01-16'
    },
    {
        id: 'q-c-003',
        subject: 'Chemistry',
        chapter: 'Inorganic Chemistry',
        difficulty: 'hard',
        text: 'Which of the following has the highest paramagnetism?',
        options: ['[Fe(CN)₆]³⁻', '[Fe(H₂O)₆]³⁺', '[Co(NH₃)₆]³⁺', '[Ni(CO)₄]'],
        correctOption: 1,
        explanation: '[Fe(H₂O)₆]³⁺ is a high spin complex with 5 unpaired electrons, showing highest paramagnetism',
        timeSuggestion: 90,
        tags: ['coordination', 'magnetism', 'd-block'],
        createdBy: 'Prof. Kumar',
        createdAt: '2026-01-19'
    },
    // Biology Questions
    {
        id: 'q-b-001',
        subject: 'Biology',
        chapter: 'Human Physiology',
        difficulty: 'easy',
        text: 'The pacemaker of the heart is:',
        options: ['AV node', 'SA node', 'Bundle of His', 'Purkinje fibers'],
        correctOption: 1,
        explanation: 'The Sinoatrial (SA) node is known as the natural pacemaker of the heart',
        timeSuggestion: 30,
        tags: ['heart', 'cardiac-cycle'],
        createdBy: 'Dr. Meera',
        createdAt: '2026-01-13'
    },
    {
        id: 'q-b-002',
        subject: 'Biology',
        chapter: 'Genetics',
        difficulty: 'medium',
        text: 'Codominance is observed in:',
        options: ['ABO blood group', 'Skin color', 'Height', 'Eye color'],
        correctOption: 0,
        explanation: 'In ABO blood group, alleles Iᴬ and Iᴮ are codominant, producing blood type AB',
        timeSuggestion: 45,
        tags: ['genetics', 'inheritance', 'blood-group'],
        createdBy: 'Dr. Meera',
        createdAt: '2026-01-15'
    },
    {
        id: 'q-b-003',
        subject: 'Biology',
        chapter: 'Cell Biology',
        difficulty: 'hard',
        text: 'During meiosis I, crossing over occurs between:',
        options: ['Sister chromatids', 'Non-sister chromatids of homologous chromosomes', 'Non-homologous chromosomes', 'Centromeres'],
        correctOption: 1,
        explanation: 'Crossing over occurs between non-sister chromatids of homologous chromosomes during prophase I',
        timeSuggestion: 60,
        tags: ['meiosis', 'cell-division', 'recombination'],
        createdBy: 'Dr. Meera',
        createdAt: '2026-01-17'
    },
    {
        id: 'q-b-004',
        subject: 'Biology',
        chapter: 'Ecology',
        difficulty: 'medium',
        text: 'The pyramid of energy in an ecosystem is always:',
        options: ['Upright', 'Inverted', 'Spindle-shaped', 'Variable'],
        correctOption: 0,
        explanation: 'Energy pyramid is always upright because energy transfer efficiency is only about 10%',
        timeSuggestion: 45,
        tags: ['ecosystem', 'energy-flow', 'ecological-pyramid'],
        createdBy: 'Dr. Meera',
        createdAt: '2026-01-20'
    }
]

// ============================================
// HELPER FUNCTIONS
// ============================================

export function getStudentById(id: string): MentorStudent | undefined {
    return mockMentorStudents.find(s => s.id === id)
}

export function getStudentsByBatch(batch: string): MentorStudent[] {
    return mockMentorStudents.filter(s => s.batch === batch)
}

export function getPendingReportsForStudent(studentId: string): StudentDailyReport[] {
    return mockPendingReports.filter(r => r.studentId === studentId && r.approvalStatus === 'pending')
}

export function getBatches(): string[] {
    return Array.from(new Set(mockMentorStudents.map(s => s.batch)))
}

export function getStudentsNeedingAttention(): MentorStudent[] {
    return mockMentorStudents.filter(s =>
        !s.todayHoursSubmitted ||
        s.dropRisk === 'high' ||
        s.currentStreak === 0 ||
        s.performanceTrend === 'declining' ||
        s.performanceTrend === 'strongly-declining'
    )
}
